<script lang="ts">
  // Overlays — global overlay primitives.
  //
  // PR 7 scope (R-Overlays from
  // openspec/changes/ui-redesign-v2/specs/dashboard-ui/spec.md):
  //
  //   - Four primitives in one file (overlays.test.ts):
  //       1. ToastSuccess — auto-dismiss after 4000ms (TDD pointer 8).
  //       2. ToastError   — manual dismiss only.
  //       3. StaleOverlay — top-right "Sin conexión" pill with
  //                         Reconnect-now button → ws.forceReconnect().
  //       4. ErrorBanner  — full-width strip + dismiss X.
  //
  //   - The component subscribes to `lib/overlays.ts` (a tiny
  //     framework-agnostic pub-sub) and re-renders on every state
  //     change. Consumers call `toastSuccess(...)`, `mountStaleOverlay()`
  //     etc. imperatively — no need to thread props through pages.
  //
  // Visual reference: openspec/changes/ui-redesign-v2/Untitled.fig
  // nodes 0:1158-1230 (StaleOverlay, ToastSuccess, ToastError,
  // ErrorBanner frames).
  //
  // WCAG 2.2 AA:
  //   - All toasts use role="status" + aria-live="polite" so screen
  //     readers announce without yanking focus. Errors use
  //     role="alert" + aria-live="assertive".
  //   - ErrorBanner is role="alert".
  //   - StaleOverlay is role="status" (persistent, not transient) and
  //     the Reconnect button is a real <button>.

  import { onDestroy } from "svelte";
  import { createWsClient, type WsClient } from "../lib/ws";
  import {
    dismissErrorBanner,
    dismissStaleOverlay,
    dismissToast,
    subscribeOverlays,
    type OverlaysState,
  } from "../lib/overlays";

  const ws: WsClient = createWsClient();

  // Synchronous initial state + module-scope subscribe (same pattern
  // as the fetch-driven pages). Module-scope
  // subscriptions fire before `render()` returns, so the test
  // environment sees the latest state without needing onMount to
  // run.
  const initial = readInitial();
  let toasts: OverlaysState["toasts"] = initial.toasts;
  let staleMounted = initial.staleMounted;
  let errorMessage = initial.errorMessage;

  function readInitial(): OverlaysState {
    let v: OverlaysState = {
      toasts: [],
      staleMounted: false,
      errorMessage: null,
    };
    const u = subscribeOverlays((next) => (v = next));
    u();
    return v;
  }

  const unsubOverlays = subscribeOverlays((next) => {
    toasts = next.toasts;
    staleMounted = next.staleMounted;
    errorMessage = next.errorMessage;
  });

  onDestroy(() => {
    unsubOverlays();
  });

  function toastAria(kind: "success" | "error"): {
    role: "status" | "alert";
    live: "polite" | "assertive";
  } {
    return kind === "error"
      ? { role: "alert", live: "assertive" }
      : { role: "status", live: "polite" };
  }

  function stackIndex(id: number): number {
    return toasts.findIndex((t) => t.id === id);
  }
</script>

<!-- Toast layer — top-right, pointer-events pass-through except on the
     toast card itself. Each toast occupies its own vertical slot via
     data-stack-index. -->
<div class="overlays-toasts" aria-live="polite">
  {#each toasts as t (t.id)}
    <div
      class="toast toast--{t.kind}"
      data-toast-id={t.id}
      data-stack-index={stackIndex(t.id)}
      data-testid="toast"
      data-kind={t.kind}
      role={toastAria(t.kind).role}
      aria-live={toastAria(t.kind).live}
    >
      <span class="toast__icon" aria-hidden="true">
        {t.kind === "success" ? "✓" : "!"}
      </span>
      <span class="toast__message">{t.message}</span>
      {#if t.kind === "error"}
        <button
          type="button"
          class="toast__dismiss"
          data-testid="toast-dismiss"
          aria-label="Dismiss error"
          on:click={() => dismissToast(t.id)}
        >×</button>
      {/if}
    </div>
  {/each}
</div>

<!-- Stale overlay — top-right, persistent until reconnect. -->
{#if staleMounted}
  <div
    class="stale-overlay"
    data-testid="stale-overlay"
    role="status"
    aria-live="polite"
  >
    <span class="stale-overlay__dot" aria-hidden="true"></span>
    <span class="stale-overlay__label">Sin conexión</span>
    <button
      type="button"
      class="stale-overlay__btn"
      data-testid="stale-reconnect"
      aria-label="Reconnect WebSocket now"
      on:click={() => ws.forceReconnect()}
    >Reconnect now</button>
  </div>
{/if}

<!-- Error banner — full-width strip above page content. -->
{#if errorMessage}
  <div
    class="error-banner"
    data-testid="error-banner"
    role="alert"
    aria-live="assertive"
  >
    <span class="error-banner__icon" aria-hidden="true">!</span>
    <span class="error-banner__message">{errorMessage}</span>
    <button
      type="button"
      class="error-banner__dismiss"
      data-testid="error-banner-dismiss"
      aria-label="Dismiss error banner"
      on:click={dismissErrorBanner}
    >×</button>
  </div>
{/if}

<style>
  /* === Toast layer ===================================================== */

  .overlays-toasts {
    position: fixed;
    top: var(--space-4);
    right: var(--space-4);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    z-index: 1000;
    pointer-events: none;
  }

  .toast {
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    min-width: 240px;
    max-width: 360px;
    padding: var(--space-3) var(--space-4);
    border-radius: var(--radius-sm);
    background: var(--surface-2);
    border: 1px solid var(--border);
    color: var(--text-strong);
    font: var(--type-body-sm);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
    pointer-events: auto;
  }

  .toast--success {
    background: var(--ok-bg);
    border-color: rgba(34, 197, 94, 0.40);
    color: var(--ok-green);
  }

  .toast--error {
    background: var(--err-bg);
    border-color: rgba(239, 68, 68, 0.40);
    color: var(--err-red);
  }

  .toast__icon {
    display: inline-flex;
    width: 20px;
    height: 20px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: currentColor;
    color: var(--bg);
    font-weight: 700;
    font-size: 12px;
    flex: 0 0 20px;
  }

  .toast__dismiss {
    margin-left: auto;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    font: var(--type-body);
    font-weight: 700;
    line-height: 1;
  }
  .toast__dismiss:hover {
    background: rgba(255, 255, 255, 0.08);
  }

  /* === Stale overlay =================================================== */

  .stale-overlay {
    position: fixed;
    top: var(--space-4);
    right: var(--space-4);
    display: inline-flex;
    align-items: center;
    gap: var(--space-3);
    padding: var(--space-3) var(--space-4);
    background: var(--warn-bg);
    border: 1px solid rgba(245, 158, 11, 0.40);
    border-radius: var(--radius-md);
    color: var(--warn-amber);
    font: var(--type-body-sm);
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
    z-index: 999;
  }
  .stale-overlay__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--warn-amber);
    box-shadow: 0 0 6px rgba(245, 158, 11, 0.55);
  }
  .stale-overlay__label {
    font-weight: 500;
  }
  .stale-overlay__btn {
    height: 24px;
    padding: 0 var(--space-3);
    border-radius: var(--radius-sm);
    background: transparent;
    border: 1px solid rgba(245, 158, 11, 0.40);
    color: var(--warn-amber);
    font: var(--type-label);
    cursor: pointer;
  }
  .stale-overlay__btn:hover {
    background: rgba(245, 158, 11, 0.10);
  }

  /* === Error banner ==================================================== */

  .error-banner {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 100%;
    padding: var(--space-3) var(--space-5);
    background: var(--err-bg);
    border-bottom: 1px solid rgba(239, 68, 68, 0.40);
    color: var(--err-red);
    font: var(--type-body-sm);
  }
  .error-banner__icon {
    display: inline-flex;
    width: 22px;
    height: 22px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.20);
    color: var(--err-red);
    font-weight: 700;
  }
  .error-banner__dismiss {
    margin-left: auto;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: transparent;
    border: 0;
    color: inherit;
    cursor: pointer;
    font: var(--type-body);
    font-weight: 700;
  }
  .error-banner__dismiss:hover {
    background: rgba(255, 255, 255, 0.08);
  }
</style>
